import {
  Download, BarChart3, PieChart, Filter, Users, HardHat, TrendingUp, Clock
} from 'lucide-react';
import { Doughnut } from 'react-chartjs-2';
import type { AnalyticsData } from './types';
import type { FilterMode } from './types';
import PeriodSelector from './PeriodSelector';

interface AnalyticsTabProps {
  analytics: AnalyticsData;
  selectedProject: string;
  setSelectedProject: (v: string) => void;
  selectedEmployee: string;
  setSelectedEmployee: (v: string) => void;
  projectList: { id: string; code: string; name: string; category?: string }[];
  employeeList: { id: string; name: string }[];
  handleExportPDFAnalytics: () => void;
  doughnutData: { labels: string[]; datasets: { data: number[]; backgroundColor: string[]; borderWidth: number }[] };
  periodSelectorProps: {
    filterMode: FilterMode;
    setFilterMode: (mode: FilterMode) => void;
    selectedYear: number;
    setSelectedYear: (year: number) => void;
    selectedMonth: number;
    setSelectedMonth: (month: number) => void;
    selectedWeek: number;
    setSelectedWeek: (week: number) => void;
    handlePrevious: () => void;
    handleNext: () => void;
    handleToday: () => void;
    periodLabel: string;
  };
}

export default function AnalyticsTab({
  analytics, selectedProject, setSelectedProject, selectedEmployee, setSelectedEmployee,
  projectList, employeeList, handleExportPDFAnalytics, doughnutData, periodSelectorProps
}: AnalyticsTabProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Sélecteur de période */}
      <PeriodSelector {...periodSelectorProps} />

      {/* Filtres additionnels */}
      <div style={{
        background: 'white', padding: '16px 20px', borderRadius: '12px', border: '1px solid #e2e8f0',
        display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' }}>
          <Filter size={18} />
          <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>Filtres :</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <HardHat size={12} /> Chantier
          </label>
          <select
            value={selectedProject}
            onChange={e => setSelectedProject(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem', minWidth: '150px', background: 'white' }}
          >
            <option value="all">Tous les chantiers</option>
            {projectList.map(p => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Users size={12} /> Collaborateur
          </label>
          <select
            value={selectedEmployee}
            onChange={e => setSelectedEmployee(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem', minWidth: '150px', background: 'white' }}
          >
            <option value="all">Tous les employés</option>
            {employeeList.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>

        <button
          onClick={handleExportPDFAnalytics}
          style={{
            padding: '10px 20px', background: '#334155', color: 'white', border: 'none',
            borderRadius: '8px', cursor: 'pointer', fontWeight: '600',
            display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto'
          }}
        >
          <Download size={16} /> PDF Rapport
        </button>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
        <div style={{
          background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
          borderRadius: '16px', padding: '20px', color: 'white',
          boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <Clock size={20} />
            <span style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', opacity: 0.9 }}>Total Heures</span>
          </div>
          <div style={{ fontSize: '2.2rem', fontWeight: '800' }}>{analytics.totalHours.toFixed(2)}</div>
          <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>heures validées</div>
        </div>

        <div style={{
          background: 'white', borderRadius: '16px', padding: '20px',
          border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <Users size={20} color="#10b981" />
            <span style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', color: '#64748b' }}>Collaborateurs</span>
          </div>
          <div style={{ fontSize: '2.2rem', fontWeight: '800', color: '#1e293b' }}>{analytics.totalEmployees}</div>
          <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>employés actifs</div>
        </div>

        <div style={{
          background: 'white', borderRadius: '16px', padding: '20px',
          border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <TrendingUp size={20} color="#f59e0b" />
            <span style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', color: '#64748b' }}>Moyenne / Pers.</span>
          </div>
          <div style={{ fontSize: '2.2rem', fontWeight: '800', color: '#1e293b' }}>{analytics.avgHoursPerEmployee.toFixed(2)}</div>
          <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>heures / collaborateur</div>
        </div>

        <div style={{
          background: 'white', borderRadius: '16px', padding: '20px',
          border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <HardHat size={20} color="#7c3aed" />
            <span style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', color: '#64748b' }}>Chantiers</span>
          </div>
          <div style={{ fontSize: '2.2rem', fontWeight: '800', color: '#1e293b' }}>{Object.keys(analytics.byProject).length}</div>
          <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>projets concernés</div>
        </div>
      </div>

      {/* Graphique + Top Chantiers — 2 colonnes */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
        {/* Colonne gauche : Camembert */}
        <div style={{ background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
          <h3 style={{ marginTop: 0, fontSize: '1.1rem', color: '#1e293b', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <PieChart size={20} color="#ea580c" /> Répartition par Type d'Activité
          </h3>
          <div style={{ height: '280px', display: 'flex', justifyContent: 'center' }}>
            <Doughnut data={doughnutData} options={{ maintainAspectRatio: false }} />
          </div>
        </div>

        {/* Colonne droite : Top Chantiers */}
        <div style={{ background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
          <h3 style={{ marginTop: 0, fontSize: '1.1rem', color: '#1e293b', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <TrendingUp size={20} color="#7c3aed" /> Top Chantiers
          </h3>
          {Object.keys(analytics.byProject).length === 0 ? (
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: '40px 0', fontSize: '0.9rem' }}>
              Aucune donnée pour cette période
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {Object.values(analytics.byProject)
                .sort((a, b) => b.total - a.total)
                .slice(0, 5)
                .map(p => {
                  const pct = analytics.totalHours > 0 ? (p.total / analytics.totalHours) * 100 : 0;
                  return (
                    <div key={p.code}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>
                          {p.name}
                        </span>
                        <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1e293b', flexShrink: 0 }}>
                          {p.total.toFixed(1)} h ({pct.toFixed(0)}%)
                        </span>
                      </div>
                      <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${Math.min(pct, 100)}%`, height: '100%',
                          background: 'linear-gradient(90deg, #7c3aed, #a78bfa)',
                          borderRadius: '4px', transition: 'width 0.5s ease'
                        }} />
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* Tableau détaillé */}
      <div style={{ background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
        <h3 style={{ marginTop: 0, fontSize: '1.1rem', color: '#1e293b', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <BarChart3 size={20} color="#2563eb" /> Détail chiffré par Chantier
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}>
                <th style={{ textAlign: 'left', padding: '12px' }}>Chantier</th>
                <th style={{ textAlign: 'left', padding: '12px' }}>Code</th>
                <th style={{ textAlign: 'center', padding: '12px', color: '#3b82f6' }}>Étude</th>
                <th style={{ textAlign: 'center', padding: '12px', color: '#f59e0b' }}>Suivi</th>
                <th style={{ textAlign: 'center', padding: '12px', color: '#8b5cf6' }}>MES</th>
                <th style={{ textAlign: 'center', padding: '12px', color: '#10b981' }}>Chantier</th>
                <th style={{ textAlign: 'right', padding: '12px', fontWeight: '800' }}>TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(analytics.byProject).sort((a, b) => b.total - a.total).map((p, idx) => (
                <tr key={p.code} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? 'white' : '#f8fafc' }}>
                  <td style={{ padding: '12px', fontWeight: '700', color: '#334155' }}>{p.name}</td>
                  <td style={{ padding: '12px', color: '#64748b' }}>{p.code}</td>
                  <td style={{ textAlign: 'center', padding: '12px', opacity: p.etude ? 1 : 0.2 }}>{p.etude.toFixed(2)}</td>
                  <td style={{ textAlign: 'center', padding: '12px', opacity: p.suivi ? 1 : 0.2 }}>{p.suivi.toFixed(2)}</td>
                  <td style={{ textAlign: 'center', padding: '12px', opacity: p.mise_en_service ? 1 : 0.2 }}>{p.mise_en_service.toFixed(2)}</td>
                  <td style={{ textAlign: 'center', padding: '12px', opacity: p.chantier ? 1 : 0.2 }}>{p.chantier.toFixed(2)}</td>
                  <td style={{ textAlign: 'right', padding: '12px', fontWeight: '700' }}>{p.total.toFixed(2)} h</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #e2e8f0', background: '#f1f5f9' }}>
                <td style={{ padding: '12px', fontWeight: '800', color: '#1e293b' }} colSpan={2}>TOTAL</td>
                <td style={{ textAlign: 'center', padding: '12px', fontWeight: '700', color: '#3b82f6' }}>{analytics.global.etude.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px', fontWeight: '700', color: '#f59e0b' }}>{analytics.global.suivi.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px', fontWeight: '700', color: '#8b5cf6' }}>{analytics.global.mise_en_service.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px', fontWeight: '700', color: '#10b981' }}>{analytics.global.chantier.toFixed(2)}</td>
                <td style={{ textAlign: 'right', padding: '12px', fontWeight: '800', color: '#1e293b' }}>{analytics.totalHours.toFixed(2)} h</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
